import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { track } from '../analytics/events';

type Ctx = { 
  calm: boolean; 
  setCalm: (v: boolean) => void; 
  highContrast: boolean; 
  setHighContrast: (v: boolean) => void 
};
const CalmCtx = createContext<Ctx | null>(null);
const K1 = 'backon.calm', K2 = 'backon.hc';

const hasWindow = typeof window !== 'undefined';
const hasDocument = typeof document !== 'undefined';

const readLocalStorageFlag = (key: string): boolean => {
  if (!hasWindow) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeLocalStorageFlag = (key: string, value: boolean) => {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore storage failures */
  }
};

const resolveInitialCalm = (): boolean => {
  if (!hasWindow) return false;
  try {
    const params = new URLSearchParams(window.location?.search ?? '');
    if (params.get('calm') === '1') return true;
  } catch {
    // ignore URL parsing errors
  }
  return readLocalStorageFlag(K1);
};

export const CalmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [calm, setCalm] = useState<boolean>(() => resolveInitialCalm());

  const [highContrast, setHighContrast] = useState<boolean>(() =>
    readLocalStorageFlag(K2)
  );

  const previousCalm = useRef(calm);

  useEffect(() => {
    if (hasDocument) {
      document.documentElement.dataset.calm = calm ? '1' : '0';
    }
    writeLocalStorageFlag(K1, calm);
    if (previousCalm.current !== calm) {
      track('calm_mode_toggled', { enabled: calm });
    }
    previousCalm.current = calm;
  }, [calm]);

  useEffect(() => {
    if (hasDocument) {
      document.documentElement.dataset.hc = highContrast ? '1' : '0';
    }
    writeLocalStorageFlag(K2, highContrast);
  }, [highContrast]);

  return <CalmCtx.Provider value={{ calm, setCalm, highContrast, setHighContrast }}>{children}</CalmCtx.Provider>;
};

export const useCalm = () => {
  const c = useContext(CalmCtx);
  if (!c) throw new Error('useCalm must be used within CalmProvider');
  return c;
};
